import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plane, CalendarDays, FileText, ShieldCheck, Camera, Users, Armchair, ListChecks,
  Sparkles, Globe2, BarChart3, CloudSun, Heart, Check, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Membership page — "Travel Smarter With My Travel Hub" (2026 pricing).
   Four annual plans:
     Individual              $39  / 1 traveler
     Couple                  $59  / 2 travelers
     Family or Small Group   $99  / up to 9 travelers
     Large Group            $199  / up to 30 travelers
   45-day free trial, no credit card. No public promotions. */

const BENEFITS = [
  { icon: CalendarDays, color: "text-blue-600", title: "All Your Trips in One Place", text: "Upcoming, current, past and dream trips — multi-city itineraries, family trips, business trips and road trips." },
  { icon: Plane, color: "text-sky-600", title: "Every Reservation Organized", text: "Flights, trains, buses, rental cars, ferries, cruises, hotels, resorts and vacation rentals — with confirmation numbers." },
  { icon: CloudSun, color: "text-cyan-600", title: "Live Flight Status & Alerts", text: "Track your flights, save them to your Hub and get gate, terminal and schedule information at a glance." },
  { icon: FileText, color: "text-emerald-600", title: "Boarding Passes & Documents", text: "Store boarding passes, confirmations, tickets and any document you need while traveling — always at hand." },
  { icon: ShieldCheck, color: "text-violet-600", title: "Passports, Visas & Health Cards", text: "Save passport and visa information with expiry reminders, plus health cards and vaccination records. Encrypted and masked." },
  { icon: Camera, color: "text-rose-600", title: "Trip Pictures & Memories", text: "Attach photos to every trip and build a beautiful private archive of everywhere you have been." },
  { icon: Users, color: "text-orange-600", title: "Traveler Profiles", text: "Keep profiles for everyone you travel with — seat preferences, dietary needs, loyalty numbers and emergency contacts." },
  { icon: Armchair, color: "text-fuchsia-600", title: "Lounge Access & Loyalty", text: "Save your Priority Pass, credit-card and airline memberships — see which lounges you can likely use at every airport." },
  { icon: ListChecks, color: "text-teal-600", title: "Packing Lists & Trip Notes", text: "Trip notes, packing lists, medication reminders and any other information you need while traveling." },
  { icon: Sparkles, color: "text-indigo-600", title: "AI Travel Assistant", text: "Ask anything — \"Which lounge can I use at Terminal 3?\", \"Create a packing list for this trip\" — answers built around YOUR journey." },
  { icon: BarChart3, color: "text-amber-600", title: "Travel Stats & History", text: "Countries visited, miles flown, airports explored — watch your personal travel map grow." },
  { icon: Globe2, color: "text-green-600", title: "Access From Anywhere", text: "Your entire journey available on any device, anywhere in the world — before, during and after every trip." },
];

const fmt = (cents) => `$${Math.round(cents / 100)}`;

export default function Membership() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (user) api.get("/memberships/me").then((r) => setMe(r.data)).catch(() => {});
  }, [user]);

  const checkout = async (plan) => {
    if (!user) { toast.info("Sign in first — your 45-day free trial starts automatically"); nav("/login"); return; }
    setBusy(plan);
    try {
      const r = await api.post("/memberships/checkout", {
        plan, founding: false, extra_members: 0, coupon_code: null,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  const trialCta = () => {
    if (!user) { nav("/login"); return; }
    nav("/travel-hub");
  };

  return (
    <div data-testid="membership-page">
      {/* Hero */}
      <section className="bg-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 sm:py-16">
          <div className="text-xs font-mono uppercase tracking-widest text-indigo-300 mb-4">My Travel Hub Membership</div>
          <h1 className="font-heading font-900 text-3xl sm:text-5xl lg:text-6xl max-w-3xl leading-tight">
            Travel Smarter With <span className="text-amber-400">My Travel Hub</span>
          </h1>
          <p className="text-indigo-100 text-base sm:text-lg mt-4 max-w-2xl">Everything about your journey — organized in one place. Your personal travel workspace for less than the cost of a coffee each month.</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button onClick={trialCta} data-testid="trial-cta"
              className="bg-amber-400 text-indigo-950 font-heading font-900 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 hover:bg-amber-300 transition-colors">
              TRY FREE FOR 45 DAYS
            </button>
            <div className="text-indigo-200 text-sm">
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> No credit card needed</div>
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> No obligation — never auto-charged</div>
            </div>
          </div>
          {me && (
            <div className="mt-6 inline-block bg-white/10 border border-white/20 px-4 py-2 text-sm" data-testid="membership-status-chip">
              {me.status === "trial" && <>Your free trial is active — <b>{me.trial_days_left} days left</b></>}
              {me.status === "active" && <>You're {/^[aeiou]/i.test(me.plan_label || "") ? "an" : "a"} <b>{me.plan_label}</b> member — thank you!</>}
              {me.status === "complimentary" && <>You have a <b>complimentary Individual membership</b> — thank you for supporting TravelerPulse!</>}
              {me.status === "expired" && <>Your trial has ended — your Hub is read-only. Subscribe below to keep planning.</>}
            </div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-2">Your Entire Journey in One Place</h2>
        <p className="text-muted-foreground mb-8 max-w-2xl">TravelerPulse stays free for discovering travel information. My Travel Hub is where YOUR travel life gets organized.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b) => (
            <div key={b.title} className="border border-zinc-300 bg-white p-5 hover:border-indigo-400 transition-colors" data-testid={`benefit-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <b.icon className={`w-7 h-7 ${b.color} mb-3`} />
              <h3 className="font-heading font-700 text-lg mb-1">{b.title}</h3>
              <p className="text-sm text-zinc-600">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing — 4 plans, no promotions */}
      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-2">Simple Annual Pricing</h2>
          <p className="text-muted-foreground mb-8">Try everything free for 45 days first. No credit card. Subscribe only if you love it.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
            <PlanCard id="individual" label="Individual" price={3900} seats="1 traveler" busy={busy} onGo={checkout}
              blurb="Your complete personal travel workspace. Share trips with viewers, helpers and Guardians without giving away your password." />
            <PlanCard id="couple" label="Couple" price={5900} seats="2 travelers" busy={busy} onGo={checkout}
              blurb="Two private profiles with one shared travel hub. Each adult uses a separate login and their own devices." />
            <PlanCard id="family" label="Family or Small Group" price={9900} seats="Family of 5 · up to 9 travelers" busy={busy} onGo={checkout}
              blurb="Prepare and travel together — a family of 5 with room for up to 9 travelers total. Group reminders, expenses, check-ins and Guardian coordination." />
            <PlanCard id="large_group" label="Groups" price={19900} seats="Up to 30 travelers" busy={busy} onGo={checkout}
              blurb="One private command center for groups of up to 30. Reunions, pilgrimages, alumni groups, clubs, weddings and volunteer trips." />
          </div>
          <p className="text-xs text-muted-foreground mt-6">All memberships are annual. Your 45-day free trial requires no credit card and is never converted automatically — you decide. Invite viewers, helpers and non-traveling Guardians without counting them as travelers. Traveler limits apply to people marked as traveling.</p>
        </div>
      </section>

      {/* Free vs Hub philosophy + Support cross-link */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid lg:grid-cols-2 gap-8">
        <div className="border border-zinc-300 bg-white p-6">
          <h3 className="font-heading font-900 text-xl mb-2">Travel information stays free</h3>
          <p className="text-sm text-zinc-600">Airlines, airports, lounges, restaurants, maps, guides, news and community discussions remain free for every traveler. My Travel Hub is the premium layer where your personal journeys, documents and AI assistance live.</p>
        </div>
        <div className="border border-rose-300 bg-rose-50/50 p-6" data-testid="support-crosslink">
          <h3 className="font-heading font-900 text-xl mb-2 flex items-center gap-2"><Heart className="w-5 h-5 text-rose-600 fill-rose-600" /> Prefer to just support us?</h3>
          <p className="text-sm text-zinc-600 mb-3">Contribute any amount toward TravelerPulse's development. Support with $25 or more and receive a complimentary year of My Travel Hub Individual membership.</p>
          <Link to="/support" data-testid="support-crosslink-btn" className="inline-flex items-center gap-2 bg-rose-600 text-white px-4 py-2 text-sm font-700 hover:bg-rose-500 transition-colors"><Heart className="w-4 h-4" /> Support TravelerPulse</Link>
        </div>
      </section>

      {me?.supporter_badge && (
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-10">
          <span className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-300 text-amber-800 px-3 py-1.5 text-sm font-700" data-testid="my-supporter-badge">
            <BadgeCheck className="w-4 h-4" /> TravelerPulse Founding Supporter — {me.supporter_badge}
          </span>
        </div>
      )}
    </div>
  );
}

function PlanCard({ id, label, price, seats, blurb, busy, onGo }) {
  return (
    <div className="border border-zinc-300 bg-white p-5 flex flex-col" data-testid={`plan-${id}`}>
      <div className="font-heading font-900 text-lg">{label}</div>
      <div className="mt-3"><span className="font-heading font-900 text-3xl">{fmt(price)}</span><span className="text-sm text-muted-foreground">/year</span></div>
      <div className="text-xs font-600 text-indigo-700 mt-1">{seats}</div>
      <p className="text-xs text-zinc-600 mt-2 flex-1">{blurb}</p>
      <button onClick={() => onGo(id)} disabled={busy} data-testid={`subscribe-${id}`}
        className="mt-4 bg-indigo-700 text-white font-700 py-2.5 text-sm hover:bg-indigo-600 disabled:opacity-50">
        {busy === id ? "Redirecting…" : `Choose ${label}`}
      </button>
    </div>
  );
}
